import { Table, addToast } from '@octano/global-ui';
import { useColumns } from '../hooks/useColumns';
import { TranslationsKeys } from '../../../../locales/translations';
import { useTranslation } from 'react-i18next';
import { useRef } from 'react';
import {
  useClassrooms,
  ITEMS_PER_PAGE_DEFAULT,
} from './Loaders/ClassroomsLoader';
import {
  RecordToEditBody,
  ClassroomApiResponse,
  ClassroomBlockingReason,
} from '../types';
import { remove, toggleStatus } from '../api';
import { getClassroomBlockingReason } from '../utils';
import ClassroomFormModal, {
  ClassroomFormModalMethods,
} from './modal/ClassroomFormModal';
import ReservationDetailsModal, {
  ReservationDetailsModalMethods,
} from './modal/ReservationDetailsModal';
import DisableDetailsModal, {
  DisableDetailsModalMethods,
} from './modal/DisableDetailsModal';
import useTexts from '../hooks/useTexts';
import { useFormData } from './SearchControlsLoader';

export default function ClassroomsTable() {
  const { t } = useTranslation(TranslationsKeys.MANAGEMENT_TOOLS_CLASSROOMS);
  const classroomModalRef = useRef<ClassroomFormModalMethods>(null);
  const reservationDetailsModalRef = useRef<ReservationDetailsModalMethods>(
    null,
  );
  const disableDetailsModalRef = useRef<DisableDetailsModalMethods>(null);
  const { data, query, updateQuery, loading, refresh } = useClassrooms();
  const { refresh: refreshFilterOptions } = useFormData();
  const texts = useTexts();

  /**
   * Crear, editar o eliminar una sala cambia el catálogo de códigos que
   * alimenta los desplegables de los filtros (`filters-data`, que se pide una
   * sola vez al montar la vista), así que hay que refrescarlo junto con el
   * listado. Habilitar y deshabilitar no lo tocan.
   */
  const reloadClassroomList = () => {
    refresh();
    refreshFilterOptions();
  };

  const handleEdit = (classroom: ClassroomApiResponse) => {
    const classroomToEdit: RecordToEditBody = {
      id: classroom.id,
      code: classroom.code,
      name: classroom.name,
      capacity: classroom.capacity,
      description: classroom.description ?? null,
      typeId: classroom.typeId,
      campusId: classroom.campusId,
      isEnabled: classroom.isEnabled,
      campus: {
        id: classroom.campusId,
        name: classroom.campus || '',
      },
      type: {
        id: parseInt(classroom.typeId),
        name: classroom.type || '',
      },
    };

    classroomModalRef.current?.open(classroomToEdit);
  };

  const handleDelete = (classroom: ClassroomApiResponse) => {
    reservationDetailsModalRef.current?.open(classroom);
  };

  /**
   * Deshabilitar pide confirmación (y puede estar bloqueado por clases o
   * eventos agendados). Habilitar es directo: es la acción que devuelve al
   * ruedo una sala que estaba en mantención y no hay nada que advertir.
   */
  const handleToggleStatus = (classroom: ClassroomApiResponse) => {
    if (classroom.isEnabled) {
      disableDetailsModalRef.current?.open(classroom);
      return;
    }
    void handleEnable(classroom);
  };

  /**
   * Traduce el 409 de la API al aviso que corresponda. Sólo se llega acá si el
   * listado estaba desactualizado; el camino normal lo corta el modal.
   */
  const notifyBlocked = (reason: ClassroomBlockingReason | null) => {
    if (reason === ClassroomBlockingReason.HasCurrentOrFutureReservations) {
      return addToast({
        icon: 'warning',
        color: 'warning',
        text: texts.delete.cannotSubtitle,
      });
    }
    if (reason === ClassroomBlockingReason.HasPastReservations) {
      return addToast({
        icon: 'warning',
        color: 'warning',
        text: texts.delete.hasPastEvents,
      });
    }
    return addToast({
      icon: 'error',
      color: 'danger',
      text: texts.form.error,
    });
  };

  const handleConfirmDelete = async (classroom: ClassroomApiResponse) => {
    // `request` no lanza: devuelve `{ error }`. Antes se asumía éxito y el
    // toast verde salía aunque la API hubiese rechazado la eliminación.
    const result = await remove(classroom.id);
    if (result.error) {
      notifyBlocked(getClassroomBlockingReason(result));
      throw result.error;
    }
    addToast({
      icon: 'success',
      color: 'success',
      text: texts.delete.success,
    });
    reloadClassroomList();
  };

  const handleConfirmDisable = async (classroom: ClassroomApiResponse) => {
    const result = await toggleStatus(classroom.id, false);
    if (result.error) {
      notifyBlocked(getClassroomBlockingReason(result));
      throw result.error;
    }
    addToast({
      icon: 'success',
      color: 'success',
      text: texts.disable.success,
    });
    refresh();
  };

  const handleEnable = async (classroom: ClassroomApiResponse) => {
    const result = await toggleStatus(classroom.id, true);
    if (result.error) {
      return addToast({
        icon: 'error',
        color: 'danger',
        text: texts.form.error,
      });
    }
    addToast({
      icon: 'success',
      color: 'success',
      text: texts.enable.success,
    });
    refresh();
  };

  const columns = useColumns({
    onEdit: handleEdit,
    onDelete: handleDelete,
    onToggleStatus: handleToggleStatus,
  });

  return (
    <>
      <Table
        columns={columns}
        data={data?.data ?? []}
        isLoadingResults={loading}
        pagination={{
          currentPage: query.page,
          itemsPerPage: ITEMS_PER_PAGE_DEFAULT,
          totalPages: data?.total_pages ?? 0,
          onChangePage: (page) => updateQuery({ ...query, page }),
          totalItems: data?.total ?? 0,
        }}
        noResultsText={t('table.noResults')}
      />

      <ClassroomFormModal
        ref={classroomModalRef}
        onSave={reloadClassroomList}
      />
      <ReservationDetailsModal
        ref={reservationDetailsModalRef}
        onConfirmDelete={handleConfirmDelete}
      />
      <DisableDetailsModal
        ref={disableDetailsModalRef}
        onConfirmDisable={handleConfirmDisable}
      />
    </>
  );
}
